import React, { useCallback, useEffect, useMemo } from 'react';
import type { Questionnaire } from '@/typings/Questionnaire';
import { Table as TableComponent, TableRow, TBody, TD, TH, THead } from '@/components/DesignLibrary/Table';
import { useQuestionnaireContext, useQuestionnaireFormContext } from '@/components/Questionnaire/QuestionnaireProvider';
import getFieldValue from '@helpers/questionnaires/getFieldValue';
import evaluateExpression from '@helpers/evaluateExpression';

import classNames from 'classnames';
import Modal from '@/components/Modal';
import _ from 'lodash';
import Shimmer from '@/components/Shimmer';
import NonListFormField from '../FormField/NonListFormField';
import s from './Table.module.scss';
import AiAgentData from '../ConnectedDataPanel/AutofillData';

type TableProps = {
  field: Questionnaire.FieldTable;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules: any;
  isEditor?: boolean;
  onFocus?: () => void;
  fieldsAutofilling?: string[];
};

const FormFieldTable: React.FC<TableProps> = ({
  field,
  control,
  rules,
  isEditor,
  onFocus,
  fieldsAutofilling,
}) => {
  const { fieldsByKey, isEnabled } = useQuestionnaireContext();
  const { formMethods } = useQuestionnaireFormContext();
  const [referencedList, setReferencedList] = React.useState<{
    title: string;
    values: Array<{
      property: string;
      value: string;
    }> | null;
    onEditClick: () => void;
    onClose: () => void;
    type?: "autofill" | "ai-autofill" | "questionnaire";
    isLoading?: boolean;
      } | null>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const conditionVariables = formMethods.watch(_.uniq(field.tableColumns
    ?.map((c) => c.conditionExpression?.variables?.map((v) => v.referenceId) || [])
    .reduce((acc, val) => [...acc, ...val], [])));

  const sortedColumns = useMemo(() => field.tableColumns?.sort((a, b) => a.rank - b.rank) || [], [field]);

  const tableColumns = useMemo(() => sortedColumns.filter((column) => {
    if (!isEnabled) {
      return true;
    }
    const { conditionExpression } = column;
    if (conditionExpression?.expression) {
      const variablesWithValues = conditionExpression.variables?.map((variable, idx) => ({
        ...variable,
        value: getFieldValue({
          field: fieldsByKey[variable.referenceId],
          value: conditionVariables[idx],
        }),
      })) || [];
      const result = evaluateExpression({
        expression: conditionExpression.expression,
        id: 'TableColumn-condition',
        variables: variablesWithValues.reduce((acc, val) => ({
          ...acc,
          [val.variableName]: val.value,
        }), {}),
      });
      return Boolean(result);
    }
    return true;
  }), [sortedColumns, conditionVariables, isEnabled, fieldsByKey]);

  const subFieldVisibility: Record<string, boolean> = useMemo(() => {
    const subFieldKeys = (field?.listItemTemplates ?? []).map((t) => t.key);
    // Subfields should be visible if at least one column's visibility condition evaluates to true for that field
    // The field should be hidden if none of the visibility conditions evaluate to true in the column
    const listItemTemplateReducer = (acc: Record<string, boolean>, subField: Questionnaire.Field): Record<string, boolean> => {
      let isVisible = true;
      if (subField.conditionExpression) {
        const columnVisibility = tableColumns.map((__, idx) => {
          if (!subField.conditionExpression) {
            return false;
          }
          const subFieldVariableValues: Record<string, unknown> = subField.conditionExpression.variables.reduce((variableAcc, subFieldVariable) => ({
            ...variableAcc,
            [subFieldVariable.referenceId]: subFieldKeys?.includes(subFieldVariable.referenceId)
              ? formMethods.getValues()[field.key]?.[idx]?.[subFieldVariable.referenceId]
              : formMethods.getValues()[subFieldVariable.referenceId],
          }), {});
          const variablesWithValues = subField.conditionExpression.variables.map(variable => ({
            ...variable,
            value: getFieldValue({
              field: fieldsByKey[variable.referenceId],
              value: subFieldVariableValues[variable.referenceId],
            }),
          }));
          const result = evaluateExpression({
            expression: subField.conditionExpression.expression,
            id: 'TableColumn-listItemTemplateReducer',
            variables: variablesWithValues.reduce(
              (variableAcc, val) => ({
                ...variableAcc,
                [val.variableName]: val.value,
              }),
              {},
            ),
          });
          return Boolean(result);
        });
        isVisible = columnVisibility.some((v) => v);
      }
      return {
        ...acc,
        ...(subField.listItemTemplates?.reduce(listItemTemplateReducer, {}) || {}),
        [subField.key]: isVisible,
      };
    };
    return (field?.listItemTemplates ?? []).reduce(listItemTemplateReducer, {});
  }, [tableColumns, field, fieldsByKey, formMethods]);

  const handleReferencedListDataUpdate: (data: {
    title: string;
    values: Array<{
      property: string;
      value: string;
    }> | null;
    onEditClick: () => void;
    onClose: () => void;
    type?: 'autofill' | 'ai-autofill' | 'questionnaire';
    isLoading?: boolean;
  } | null) => void = (data) => {
    setReferencedList(data);
  };

  const renderSubField = useCallback(({ isSectionSubField, subField }: {
    isSectionSubField?: boolean;
    subField: Questionnaire.Field;
  }) => (
    <TableRow key={subField.id}>
      <TD className={isSectionSubField ? s.sectionFieldCentered : ''}>
        <p className={isSectionSubField ? 'caption' : 'body-sm'} style={isSectionSubField ? { color: 'var(--color-gray-75)' } : {}}>{subField.label}</p>
        <p className="caption" style={{ color: 'var(--color-gray-75)' }}>{subField.description}</p>
      </TD>
      {tableColumns.map((column) => {
        const originalColumnIndex = sortedColumns.findIndex((c) => c.id === column.id);
        return (
          <TD key={column.id} className={classNames(s.tableField, isSectionSubField ? s.sectionField : '')}>
            <NonListFormField
              key={subField.id}
              listField={field}
              index={originalColumnIndex}
              field={{
                ...subField,
                description: '',
                descriptionText: '',
                key: `${field.key}[${originalColumnIndex}].${subField.key}`,
                label: '',
                linkedFields: fieldsByKey[subField.key]?.linkedFields?.filter((f) => f.linkedFieldTemplateIndex === originalColumnIndex),
                listItemTemplates: Object.values(fieldsByKey)
                  .filter((f) => f.listFieldTemplateId === subField.id)
                  .sort((a, b) => a.rank - b.rank),
                ...subField.type === 'single-select' && { native: true, placeholder: 'Select an option', type: 'selection' },
                ...subField.type === 'currency' && { nativeSelect: true },
                ...subField.type === 'date' && { fixed: true },
              }}
              control={control}
              rules={rules[subField.key]}
              fieldsAutofilling={fieldsAutofilling}
              handleReferencedListDataUpdate={handleReferencedListDataUpdate}
            />
          </TD>
        );
      })}
    </TableRow>
  ), [control, field, rules, sortedColumns, tableColumns, fieldsAutofilling, fieldsByKey]);

  useEffect(() => {
    if (onFocus) {
      const handleFocus = (event: FocusEvent): void => {
        if (containerRef.current?.contains(event.target as Node)) {
          onFocus();
        }
      };

      document.addEventListener('focus', handleFocus, true);

      return () => {
        document.removeEventListener('focus', handleFocus, true);
      };
    }

    return undefined;
  }, [onFocus]);

  return (
    <div className={s.tableContainer} ref={containerRef}>
      <TableComponent className={s.table}>
        <THead>
          <TableRow>
            <TH label={field.label} />
            {tableColumns.map((column) => (
              <TH key={column.id} label={column.label} />
            ))}
          </TableRow>
        </THead>
        <TBody>
          {!isEditor && field.listItemTemplates?.filter((subField) => subFieldVisibility[subField.key])?.map((subField) => {
            if (subField.type === 'explanation') {
              return (
                <React.Fragment key={subField.id}>
                  <TableRow>
                    <TD className={s.sectionColumn}>
                      <p className="body-sm">{subField.label}</p>
                      <p className="caption" style={{ color: 'var(--color-gray-75)' }}>{subField.description}</p>
                    </TD>
                    {tableColumns.map((c) => <TD key={c.id} className={s.sectionColumn} />)}
                  </TableRow>
                  {(subField?.listItemTemplates ?? [])
                    .filter((subFieldTemplate) => subFieldVisibility[subFieldTemplate.key])
                    .map((sectionSubField) => renderSubField({
                      isSectionSubField: true,
                      subField: sectionSubField,
                    }))}
                </React.Fragment>
              );
            }
            return renderSubField({
              subField,
            });
          })}
        </TBody>
      </TableComponent>
      {
        referencedList?.isLoading && (
          <Modal title={`${field.label} Data`}>
            <Shimmer className={s.shimmer} />
          </Modal>
        )
      }
      {
        (referencedList && !referencedList?.isLoading) && (
          <Modal onClose={() => setReferencedList(null)} title={`${field.label} Data`}>
            <div className={s.modalContent}>
              <AiAgentData data={referencedList} hideCloseIcon={true} field={field} />
            </div>
          </Modal>
        )
      }
    </div>
  );
};

export default FormFieldTable;
